import { SearchFacetValue, SearchRequest, SearchResults } from '../interfaces/search.interface';

/**
 * Sorts the facets based on the facet name.
 *
 * @param name
 * @param facet1
 * @param facet2
 */
export function facetSorter(name: string, facet1: SearchFacetValue, facet2: SearchFacetValue) {
  if (name === '_Elementary School') {
    return elementarySchoolSort(facet1, facet2);
  } else if (name === '_High School') {
    return highSchoolSort(facet1, facet2);
  } else if (name === 'PRICE_RANGE') {
    return PriceRangeSort(facet1, facet2);
  } else {
    return defaultSort(facet1, facet2);
  }
}

/**
 * Sorts the elementary school facet.
 *
 * @param facet1
 * @param facet2
 */
function elementarySchoolSort(facet1: SearchFacetValue, facet2: SearchFacetValue) {
  const order = ['_Elementary School_Kindergarten'];
  const index1 = order.indexOf(facet1.name);
  const index2 = order.indexOf(facet2.name);

  if (index1 !== -1 || index2 !== -1) {
    return index1 < index2 ? 1 : -1;
  } else {
    return defaultSort(facet1, facet2);
  }
}

/**
 * Sorts the high school facet.
 *
 * @param facet1
 * @param facet2
 */
function highSchoolSort(facet1: SearchFacetValue, facet2: SearchFacetValue) {
  const order = [
    '_High School_9th Grade',
    '_High School_10th Grade',
    '_High School_11th Grade',
    '_High School_12th Grade',
  ];
  return order.indexOf(facet1.name) > order.indexOf(facet2.name) ? 1 : -1;
}

/**
 * Sorts the price range facet.
 *
 * @param facet1
 * @param facet2
 * @constructor
 */
function PriceRangeSort(facet1: SearchFacetValue, facet2: SearchFacetValue) {
  return getPriceSubstring(facet1.name, '$', '-$') > getPriceSubstring(facet2.name, '$', '-$') ? 1 : -1;
}

/**
 * Gets the price substring from a string.
 *
 * @param string
 * @param char1
 * @param char2
 */
function getPriceSubstring(string, char1, char2) {
  return Number(string?.slice(string.indexOf(char1) + 1, string.lastIndexOf(char2))?.replaceAll(',', ''));
}

/**
 * Default facet sort function.
 *
 * @param facet1
 * @param facet2
 */
function defaultSort(facet1: SearchFacetValue, facet2: SearchFacetValue) {
  return facet1.label > facet2.label ? 1 : -1;
}

/**
 * Removes any filters that are not available in the results.
 *
 * @param request The request that might have invalid filters.
 * @param results The results of the request.
 *
 * @returns A new request with any invalid filters removed.
 */
export function removeUnavailableFacets(request: SearchRequest, results: SearchResults): SearchRequest | undefined {
  const filters = request.fieldValueFilter;
  Object.keys(filters || {}).forEach((key) => {
    const facet = results.facets?.find((facet) => facet.name === key);
    const values: Array<string> = [];

    facet?.facetValues.forEach((value) => {
      values.push(value.name);
      value.children?.forEach((child) => {
        values.push(child.name);
      });
    });

    if (filters?.[key]) {
      filters[key] = filters[key].filter((filterVal) => values.includes(filterVal));
      // If no values remain after filtering, delete the key entirely to prevent
      // sending malformed requests like { fieldValueFilter: { CONTENT_TAG_PATH: [] } }
      // which the backend rejects with 400 "both facet name and facet values names are required".
      if (filters[key].length === 0) {
        delete filters[key];
      }
    }
  });

  request.fieldValueFilter = filters;
  return request;
}
